/**
 * Paragraph structure helpers for RichTextEditor.
 * Each visual line is stored as its own direct-child <div> so alignment
 * can be applied per line instead of to the whole block.
 */

export type TextBlockAlign = 'left' | 'right' | 'justify' | 'justify-right';

/** Marker chars when the entire line is blank (Word empty list line). */
const LIST_MARKER_ONLY_CHARS =
  '[-–—\u2010\u2011\u2212•·\u2022\u2043\u2219\uf0b7\uf076\u00b7]';

const LIST_MARKER_ONLY = new RegExp(
  `^[\\s\\u00a0\\u200B]*(?:${LIST_MARKER_ONLY_CHARS}|[oO])\\s*$`,
  'u'
);

/** True when text is only a list marker (Word empty list line, etc.). */
export const isListMarkerOnlyText = (text: string | null | undefined): boolean =>
  LIST_MARKER_ONLY.test(text ?? '');

/** Bullets stripped from Word list item starts — not ASCII hyphen (user-typed dashes). */
const LIST_BULLET_LEADING_CHARS = '[•·\u2022\u2043\u2219\uf0b7\uf076\u00b7]';

export const LIST_MARKER_LEADING = new RegExp(
  `^[\\s\\u00a0\\u200B]*(?:${LIST_BULLET_LEADING_CHARS}|\\d+[.)])\\s*`,
  'u'
);

/** Invisible character so clicks at line end land in a text node (RTL/LTR). */
export const CARET_ZWSP = '\u200B';

export const getTextBlockAlignmentStyle = (
  align: TextBlockAlign,
  options?: { rtl?: boolean }
): string => {
  if (align === 'justify-right') {
    return 'text-align: justify; text-align-last: right; direction: rtl;';
  }
  if (align === 'justify') {
    return 'text-align: justify; text-align-last: auto; direction: ltr;';
  }
  if (align === 'right' && options?.rtl) {
    return 'text-align: right; text-align-last: auto; direction: rtl;';
  }
  if (align === 'left' && options?.rtl) {
    return 'text-align: left; text-align-last: auto; direction: rtl;';
  }
  return `text-align: ${align}; text-align-last: auto; direction: ltr;`;
};

export const stripCaretAnchors = (text: string): string => text.replace(/\u200B/g, '');

/** Paragraph gap — keep editor and learner preview in sync (CSS only, never inline). */
export const RTE_PARAGRAPH_GAP = '0.35em';

export const readParagraphRtl = (div: HTMLDivElement): boolean => {
  if (div.getAttribute('data-direction') === 'rtl') return true;
  const style = div.getAttribute('style') || '';
  return /direction\s*:\s*rtl/i.test(style);
};

const STRIPPED_PARAGRAPH_STYLE_KEYS = new Set([
  'margin-bottom',
  'margin-top',
  'margin',
  'display',
  'line-height',
  'padding-top',
  'padding-bottom',
  'padding',
  'mso-line-height-alt',
  'mso-margin-top-alt',
  'mso-margin-bottom-alt',
]);

export const stripParagraphLayoutStyles = (style: string): string => {
  return style
    .split(';')
    .map((part) => part.trim())
    .filter((part) => {
      if (!part) return false;
      const key = part.split(':')[0]?.trim().toLowerCase();
      return !STRIPPED_PARAGRAPH_STYLE_KEYS.has(key);
    })
    .join('; ');
};

export const copyParagraphAttributes = (
  target: HTMLDivElement,
  source: HTMLDivElement | null
): void => {
  if (!source) return;
  const align = source.getAttribute('data-align');
  const direction = source.getAttribute('data-direction');
  const style = source.getAttribute('style');
  if (align) target.setAttribute('data-align', align);
  else target.removeAttribute('data-align');
  if (direction) target.setAttribute('data-direction', direction);
  else target.removeAttribute('data-direction');
  if (style) target.setAttribute('style', style);
  else target.removeAttribute('style');
};

export const createParagraphDiv = (template?: HTMLDivElement | null): HTMLDivElement => {
  const div = document.createElement('div');
  if (template) {
    copyParagraphAttributes(div, template);
  }
  return div;
};

/**
 * Persist paragraph direction from the admin editor (dir=auto) into saved HTML so
 * learner view matches content management regardless of UI locale.
 */
export const stampAuthoredParagraphDirections = (editor: HTMLDivElement): void => {
  for (const child of Array.from(editor.children)) {
    if (child.nodeType !== Node.ELEMENT_NODE) continue;
    const tag = (child as Element).tagName;
    if (tag !== 'DIV' && tag !== 'P') continue;

    const div = child as HTMLDivElement;
    if (readParagraphRtl(div)) continue;

    const visible = stripCaretAnchors(div.textContent ?? '').trim();
    if (!visible) continue;

    if (window.getComputedStyle(div).direction !== 'rtl') continue;

    div.setAttribute('data-direction', 'rtl');
    const align = div.getAttribute('data-align') as TextBlockAlign | null;
    if (align) {
      div.setAttribute('style', getTextBlockAlignmentStyle(align, { rtl: true }));
    }
  }
};

export const applyParagraphAlignment = (div: HTMLDivElement, align: TextBlockAlign): void => {
  const hadRtl = readParagraphRtl(div);
  div.setAttribute('data-align', align);

  if (align === 'justify-right') {
    div.setAttribute('data-direction', 'rtl');
  } else if (align === 'right' && hadRtl) {
    div.setAttribute('data-direction', 'rtl');
  } else if (align === 'left' && hadRtl) {
    div.setAttribute('data-direction', 'rtl');
  } else if (align === 'left' || align === 'justify') {
    div.removeAttribute('data-direction');
  } else if (align === 'right') {
    div.removeAttribute('data-direction');
  }

  const rtl = div.getAttribute('data-direction') === 'rtl';
  div.setAttribute('style', getTextBlockAlignmentStyle(align, { rtl }));
};

const isCaretAnchorNode = (node: Node | null | undefined): boolean => {
  return !!node && node.nodeType === Node.TEXT_NODE && (node.textContent ?? '') === CARET_ZWSP;
};

const removeTrailingCaretAnchor = (div: HTMLDivElement): void => {
  const last = div.lastChild;
  if (isCaretAnchorNode(last)) {
    last?.remove();
    return;
  }
  if (last?.nodeType === Node.TEXT_NODE) {
    const text = last.textContent ?? '';
    if (text.endsWith(CARET_ZWSP)) {
      const trimmed = text.slice(0, -1);
      if (trimmed) {
        last.textContent = trimmed;
      } else {
        last.remove();
      }
    }
  }
};

/** ZWSP caret target only on empty paragraphs — avoids breaking mouse text selection. */
export const ensureParagraphCaretAnchors = (editor: HTMLDivElement): void => {
  for (const child of Array.from(editor.children)) {
    if (child.nodeType !== Node.ELEMENT_NODE || (child as Element).tagName !== 'DIV') continue;
    const div = child as HTMLDivElement;
    const visibleLength = stripCaretAnchors(div.textContent ?? '').length;

    if (visibleLength > 0) {
      removeTrailingCaretAnchor(div);
      continue;
    }

    if (!isCaretAnchorNode(div.lastChild as Node | null)) {
      div.appendChild(document.createTextNode(CARET_ZWSP));
    }
  }
};

const isBlankLineDiv = (div: HTMLDivElement): boolean => {
  const raw = stripCaretAnchors(div.textContent ?? '').replace(/\u00a0/g, '');
  if (isListMarkerOnlyText(raw)) return true;
  if (raw.trim()) return false;
  if (div.querySelector('table, ul, ol, img')) return false;
  return true;
};

const normalizeBlankLineDiv = (div: HTMLDivElement): void => {
  div.textContent = '';
  div.removeAttribute('data-list-item');
  div.removeAttribute('data-list-level');
  div.appendChild(document.createElement('br'));
};

const cleanMarkerOnlyParagraphs = (editor: HTMLDivElement): void => {
  for (const child of Array.from(editor.children)) {
    if (child.nodeType !== Node.ELEMENT_NODE || (child as Element).tagName !== 'DIV') continue;
    const div = child as HTMLDivElement;
    if (div.querySelector('table, ul, ol, img')) continue;
    if (isBlankLineDiv(div)) {
      normalizeBlankLineDiv(div);
    }
  }
};

const splitDivOnBr = (element: HTMLDivElement): HTMLDivElement[] => {
  const template = element;
  const paragraphs: HTMLDivElement[] = [];
  let current = createParagraphDiv(template);

  for (const child of Array.from(element.childNodes)) {
    if (isCaretAnchorNode(child)) continue;
    if (child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName === 'BR') {
      if (current.childNodes.length > 0) {
        paragraphs.push(current);
        current = createParagraphDiv(template);
      } else {
        const blank = createParagraphDiv(template);
        blank.appendChild(document.createElement('br'));
        paragraphs.push(blank);
      }
    } else {
      current.appendChild(child);
    }
  }

  if (current.childNodes.length > 0) {
    paragraphs.push(current);
  }

  return paragraphs;
};

/** Word paste often wraps lines in an outer div — hoist inner paragraph divs to the editor root. */
export const flattenDirectChildWrappers = (editor: HTMLDivElement): void => {
  let changed = true;
  while (changed) {
    changed = false;
    for (const child of Array.from(editor.children)) {
      if (child.nodeType !== Node.ELEMENT_NODE || (child as Element).tagName !== 'DIV') continue;
      const wrapper = child as HTMLDivElement;
      if (wrapper.children.length === 0) continue;

      const hasDirectText = Array.from(wrapper.childNodes).some(
        (node) => node.nodeType === Node.TEXT_NODE && stripCaretAnchors(node.textContent ?? '').length > 0
      );
      if (hasDirectText) continue;

      const onlyDivChildren = Array.from(wrapper.childNodes).every(
        (node) => node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'DIV'
      );
      if (!onlyDivChildren) continue;

      const wrapperAlign = wrapper.getAttribute('data-align') as TextBlockAlign | null;
      const wrapperRtl = readParagraphRtl(wrapper);

      while (wrapper.firstChild) {
        const inner = wrapper.firstChild as HTMLDivElement;
        if (inner.nodeType === Node.ELEMENT_NODE && !inner.getAttribute('data-align')) {
          if (wrapperAlign) {
            applyParagraphAlignment(inner, wrapperAlign);
          } else if (wrapperRtl) {
            inner.setAttribute('data-direction', 'rtl');
            applyParagraphAlignment(inner, 'right');
          }
        } else if (
          inner.nodeType === Node.ELEMENT_NODE &&
          wrapperRtl &&
          !inner.getAttribute('data-direction') &&
          inner.getAttribute('data-align') === 'right'
        ) {
          inner.setAttribute('data-direction', 'rtl');
          applyParagraphAlignment(inner, 'right');
        }
        editor.insertBefore(inner, wrapper);
      }
      wrapper.remove();
      changed = true;
      break;
    }
  }
};

const hasWrapperOnlyDivs = (editor: HTMLDivElement): boolean => {
  for (const child of editor.children) {
    if (child.nodeType !== Node.ELEMENT_NODE || (child as Element).tagName !== 'DIV') continue;
    const div = child as HTMLDivElement;
    if (div.children.length === 0) continue;
    const hasDirectText = Array.from(div.childNodes).some(
      (node) => node.nodeType === Node.TEXT_NODE && stripCaretAnchors(node.textContent ?? '').length > 0
    );
    if (hasDirectText) continue;
    if (Array.from(div.childNodes).every(
      (node) => node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'DIV'
    )) {
      return true;
    }
  }
  return false;
};

/**
 * Wrap direct text/inline nodes in a paragraph div so block paste inserts at the caret,
 * not at the start of the editor root.
 */
export const wrapLooseInlineContentInParagraph = (editor: HTMLDivElement): void => {
  const needsWrap = Array.from(editor.childNodes).some((node) => {
    if (isCaretAnchorNode(node)) return false;
    if (node.nodeType === Node.TEXT_NODE) {
      return stripCaretAnchors(node.textContent ?? '').length > 0;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      return (node as Element).tagName !== 'DIV';
    }
    return true;
  });
  if (!needsWrap) return;

  const wrapper = createParagraphDiv();
  Array.from(editor.childNodes).forEach((node) => wrapper.appendChild(node));
  editor.appendChild(wrapper);
};

export const needsParagraphNormalize = (editor: HTMLDivElement): boolean => {
  if (hasWrapperOnlyDivs(editor)) return true;
  if (editor.querySelector('br')) return true;
  for (const child of editor.childNodes) {
    if (child.nodeType === Node.TEXT_NODE && stripCaretAnchors(child.textContent ?? '').length > 0) {
      return true;
    }
    if (child.nodeType !== Node.ELEMENT_NODE || (child as Element).tagName !== 'DIV') {
      return true;
    }
    if ((child as HTMLDivElement).querySelector('br')) return true;
  }
  return false;
};

/**
 * Ensures each line is a direct-child paragraph div (splits on <br>).
 */
export const normalizeEditorParagraphs = (editor: HTMLDivElement): void => {
  flattenDirectChildWrappers(editor);

  if (!needsParagraphNormalize(editor)) {
    cleanMarkerOnlyParagraphs(editor);
    ensureParagraphCaretAnchors(editor);
    return;
  }

  const paragraphs: HTMLDivElement[] = [];
  let inlineNodes: Node[] = [];

  const flushInline = () => {
    const nodes = inlineNodes.filter((node) => !isCaretAnchorNode(node));
    inlineNodes = [];
    if (nodes.length === 0) return;
    const div = createParagraphDiv();
    nodes.forEach((node) => div.appendChild(node));
    paragraphs.push(div);
  };

  for (const child of Array.from(editor.childNodes)) {
    if (isCaretAnchorNode(child)) continue;

    if (child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName === 'BR') {
      flushInline();
      const blank = createParagraphDiv();
      blank.appendChild(document.createElement('br'));
      paragraphs.push(blank);
      continue;
    }

    if (child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName === 'DIV') {
      flushInline();
      const div = child as HTMLDivElement;
      if (div.querySelector('br')) {
        paragraphs.push(...splitDivOnBr(div));
      } else if (
        isBlankLineDiv(div) ||
        stripCaretAnchors(div.textContent ?? '').replace(/\u00a0/g, '').length > 0 ||
        div.querySelector('table, ul, ol, img')
      ) {
        if (isBlankLineDiv(div)) {
          normalizeBlankLineDiv(div);
        }
        paragraphs.push(div);
      }
      continue;
    }

    inlineNodes.push(child);
  }

  flushInline();

  editor.innerHTML = '';
  paragraphs.forEach((paragraph) => editor.appendChild(paragraph));
  flattenDirectChildWrappers(editor);
  cleanMarkerOnlyParagraphs(editor);
  ensureParagraphCaretAnchors(editor);
};

export const findParagraphDivAtPoint = (
  editor: HTMLElement,
  node: Node | null | undefined
): HTMLDivElement | null => {
  if (!node) return null;
  let current: Node | null = node;
  while (current && current !== editor) {
    if (
      current.nodeType === Node.ELEMENT_NODE &&
      (current as HTMLElement).tagName === 'DIV' &&
      (current as HTMLElement).parentElement === editor
    ) {
      return current as HTMLDivElement;
    }
    current = current.parentNode;
  }
  return null;
};

/** Paragraph div containing a character offset (ignores caret zwsp). */
export const findParagraphDivForOffset = (
  editor: HTMLDivElement,
  offset: number
): HTMLDivElement | null => {
  const paragraphs = Array.from(editor.children).filter(
    (child): child is HTMLDivElement =>
      child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName === 'DIV'
  );

  if (paragraphs.length === 0) return null;

  let pos = 0;
  for (const paragraph of paragraphs) {
    const len = stripCaretAnchors(paragraph.textContent ?? '').length;
    if (len === 0) {
      if (offset === pos) return paragraph;
    } else if (offset >= pos && offset <= pos + len) {
      return paragraph;
    }
    pos += len;
  }

  return paragraphs[paragraphs.length - 1] ?? null;
};

/** Paragraph divs overlapping a character range (ignores caret zwsp). */
export const findParagraphDivsForOffsets = (
  editor: HTMLDivElement,
  start: number,
  end: number
): HTMLDivElement[] => {
  if (start === end) {
    const one = findParagraphDivForOffset(editor, start);
    return one ? [one] : [];
  }

  const paragraphs = Array.from(editor.children).filter(
    (child): child is HTMLDivElement =>
      child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName === 'DIV'
  );

  const hits: HTMLDivElement[] = [];
  let pos = 0;

  for (const paragraph of paragraphs) {
    const len = stripCaretAnchors(paragraph.textContent ?? '').length;
    const pStart = pos;
    const pEnd = pos + len;
    if (start < pEnd && end > pStart) {
      hits.push(paragraph);
    }
    pos = pEnd;
  }

  return hits;
};

/** Character offset of range start within editor text (ignores caret zwsp). */
export const saveSelectionTextOffset = (editor: HTMLElement, range: Range): number => {
  if (!range.startContainer) return 0;
  try {
    const preRange = document.createRange();
    preRange.selectNodeContents(editor);
    preRange.setEnd(range.startContainer, range.startOffset);
    return stripCaretAnchors(preRange.toString()).length;
  } catch {
    return 0;
  }
};

/** Plain text from editor DOM in document order (caret ZWSP anchors excluded). */
export const getEditorVisiblePlainText = (editor: HTMLElement): string => {
  const parts: string[] = [];
  const walk = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const visible = stripCaretAnchors(node.textContent ?? '');
      if (visible) parts.push(visible);
      return;
    }
    for (const child of Array.from(node.childNodes)) walk(child);
  };
  walk(editor);
  return parts.join('');
};

/** Selected plain text and offsets aligned with {@link getEditorVisiblePlainText}. */
export const measureSelectionInEditor = (
  editor: HTMLElement,
  range: Range
): { start: number; end: number; text: string } => {
  const start = saveSelectionTextOffset(editor, range);
  const endRange = document.createRange();
  endRange.selectNodeContents(editor);
  endRange.setEnd(range.endContainer, range.endOffset);
  const end = Math.max(start, stripCaretAnchors(endRange.toString()).length);
  const plain = getEditorVisiblePlainText(editor);
  const rangeText = stripCaretAnchors(range.toString());
  const text = rangeText || plain.substring(start, end);
  return { start, end, text };
};

/** Save both edges when the user has highlighted text. */
export const saveSelectionTextOffsets = (
  editor: HTMLElement,
  range: Range
): { start: number; end: number } => {
  if (!range.startContainer || !range.endContainer) {
    return { start: 0, end: 0 };
  }
  try {
    const endRange = document.createRange();
    endRange.selectNodeContents(editor);
    endRange.setEnd(range.endContainer, range.endOffset);
    const start = saveSelectionTextOffset(editor, range);
    const end = stripCaretAnchors(endRange.toString()).length;
    return { start, end: Math.max(start, end) };
  } catch {
    return { start: 0, end: 0 };
  }
};

export const restoreSelectionTextOffset = (editor: HTMLElement, offset: number): void => {
  const selection = window.getSelection();
  if (!selection) return;

  try {
    const range = document.createRange();
    let remaining = Math.max(0, offset);
    let placed = false;

    const walk = (node: Node): void => {
      if (placed) return;

      if (node.nodeType === Node.TEXT_NODE) {
        const raw = node.textContent ?? '';
        const visible = stripCaretAnchors(raw);
        if (visible.length === 0) return;

        if (remaining <= visible.length) {
          let pos = 0;
          let seen = 0;
          for (let i = 0; i < raw.length; i++) {
            if (raw[i] === CARET_ZWSP) continue;
            if (seen === remaining) {
              pos = i;
              break;
            }
            seen++;
            pos = i + 1;
          }
          range.setStart(node, pos);
          range.collapse(true);
          placed = true;
        } else {
          remaining -= visible.length;
        }
        return;
      }

      for (const child of Array.from(node.childNodes)) {
        walk(child);
        if (placed) return;
      }
    };

    walk(editor);

    if (!placed) {
      placeCaretAtVisibleEnd(editor, range);
    }

    selection.removeAllRanges();
    selection.addRange(range);
  } catch {
    // Stale selection after DOM rebuild — ignore
  }
};

export const restoreSelectionTextOffsets = (
  editor: HTMLElement,
  start: number,
  end: number
): void => {
  if (start === end) {
    restoreSelectionTextOffset(editor, start);
    return;
  }

  const selection = window.getSelection();
  if (!selection) return;

  try {
    const startRange = document.createRange();
    const endRange = document.createRange();

    const placeBoundary = (targetOffset: number, targetRange: Range): boolean => {
      let remaining = targetOffset;
      let placed = false;

      const walk = (node: Node): void => {
        if (placed) return;
        if (node.nodeType === Node.TEXT_NODE) {
          const raw = node.textContent ?? '';
          const visible = stripCaretAnchors(raw);
          if (visible.length === 0) return;

          if (remaining <= visible.length) {
            let pos = 0;
            let seen = 0;
            for (let i = 0; i < raw.length; i++) {
              if (raw[i] === CARET_ZWSP) continue;
              if (seen === remaining) {
                pos = i;
                break;
              }
              seen++;
              pos = i + 1;
            }
            targetRange.setStart(node, pos);
            targetRange.collapse(true);
            placed = true;
          } else {
            remaining -= visible.length;
          }
          return;
        }
        for (const child of Array.from(node.childNodes)) {
          walk(child);
          if (placed) return;
        }
      };

      walk(editor);
      return placed;
    };

    if (!placeBoundary(start, startRange)) {
      placeCaretAtVisibleEnd(editor, startRange);
    }
    if (!placeBoundary(end, endRange)) {
      placeCaretAtVisibleEnd(editor, endRange);
    }

    const range = document.createRange();
    range.setStart(startRange.startContainer, startRange.startOffset);
    range.setEnd(endRange.startContainer, endRange.startOffset);
    selection.removeAllRanges();
    selection.addRange(range);
  } catch {
    // Stale selection after DOM rebuild — ignore
  }
};

const placeCaretAtVisibleEnd = (editor: HTMLElement, range: Range): void => {
  const paragraphs = Array.from(editor.children).filter(
    (child): child is HTMLDivElement =>
      child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName === 'DIV'
  );
  const lastParagraph = paragraphs[paragraphs.length - 1];
  if (!lastParagraph) {
    range.selectNodeContents(editor);
    range.collapse(false);
    return;
  }

  if (isCaretAnchorNode(lastParagraph.lastChild as Node)) {
    range.setStart(lastParagraph.lastChild as Text, 0);
    range.collapse(true);
    return;
  }

  range.selectNodeContents(lastParagraph);
  range.collapse(false);
};

/** Place caret at the visible end of a paragraph (before zwsp anchor). */
export const placeCaretAtParagraphEnd = (paragraph: HTMLDivElement): void => {
  const selection = window.getSelection();
  if (!selection) return;

  const range = document.createRange();
  if (isCaretAnchorNode(paragraph.lastChild as Node)) {
    range.setStart(paragraph.lastChild as Text, 0);
    range.collapse(true);
  } else {
    range.selectNodeContents(paragraph);
    range.collapse(false);
  }
  selection.removeAllRanges();
  selection.addRange(range);
};

export const PASTE_MARKER_ATTR = 'data-rte-paste-marker';
export const PASTE_MARKER_SELECTOR = `[${PASTE_MARKER_ATTR}]`;

/** Remove invisible paste caret markers from the editor. */
export const removePasteMarkers = (editor: HTMLElement): void => {
  editor.querySelectorAll(`[${PASTE_MARKER_ATTR}]`).forEach((el) => el.remove());
};

/**
 * Insert an invisible marker at the caret so paste can target this exact DOM
 * position (works with blank lines from Enter and text below the caret).
 */
export const placePasteMarker = (editor: HTMLElement, range: Range): boolean => {
  removePasteMarkers(editor);
  try {
    const position = range.cloneRange();
    position.collapse(true);
    const marker = document.createElement('span');
    marker.setAttribute(PASTE_MARKER_ATTR, '1');
    marker.appendChild(document.createTextNode('\uFEFF'));
    position.insertNode(marker);
    return editor.contains(marker);
  } catch {
    return false;
  }
};

const cloneBlockDiv = (source: HTMLDivElement): HTMLDivElement => {
  const clone = createParagraphDiv(source);
  clone.innerHTML = source.innerHTML;
  if (!stripCaretAnchors(clone.textContent ?? '').length && !clone.querySelector('br')) {
    clone.appendChild(document.createElement('br'));
  }
  return clone;
};

/** Restore caret before paste insert (bookmark beats stale marker/range after modal blur). */
export const restorePasteCaretForInsert = (
  editor: HTMLDivElement,
  bookmark: EditorCaretBookmark | null,
  savedRange: Range | null
): Range | null => {
  editor.focus();
  removePasteMarkers(editor);

  const selection = window.getSelection();
  if (!selection) return null;

  wrapLooseInlineContentInParagraph(editor);

  if (bookmark && restoreEditorCaretBookmark(editor, bookmark)) {
    if (selection.rangeCount > 0) {
      return selection.getRangeAt(0).cloneRange();
    }
  }

  if (savedRange) {
    try {
      if (editor.contains(savedRange.commonAncestorContainer)) {
        selection.removeAllRanges();
        selection.addRange(savedRange);
        return savedRange.cloneRange();
      }
    } catch {
      /* range detached after DOM rewrite */
    }
  }

  if (selection.rangeCount > 0) {
    const live = selection.getRangeAt(0);
    if (editor.contains(live.commonAncestorContainer)) {
      return live.cloneRange();
    }
  }

  return null;
};

/**
 * Insert sanitized HTML at the caret (use {@link restorePasteCaretForInsert} range when possible).
 * Block <div> paste is inserted as editor paragraphs at the caret line (not editor top).
 */
export const insertContentAtCaret = (
  editor: HTMLDivElement,
  html: string,
  insertRange?: Range | null
): boolean => {
  const selection = window.getSelection();
  if (!selection) return false;

  let range: Range;
  if (insertRange && editor.contains(insertRange.commonAncestorContainer)) {
    range = insertRange.cloneRange();
    range.collapse(true);
  } else {
    const marker = editor.querySelector(PASTE_MARKER_SELECTOR);
    if (!marker || !editor.contains(marker)) return false;
    range = document.createRange();
    range.setStartBefore(marker);
    range.collapse(true);
    marker.remove();
  }

  selection.removeAllRanges();
  selection.addRange(range);

  const temp = document.createElement('div');
  temp.innerHTML = html;
  const nodes = Array.from(temp.childNodes);
  const blockDivs = nodes.filter(
    (n): n is HTMLDivElement =>
      n.nodeType === Node.ELEMENT_NODE && (n as Element).tagName === 'DIV'
  );
  const allTopLevelBlocks = nodes.length > 0 && blockDivs.length === nodes.length;

  if (allTopLevelBlocks) {
    let para = findParagraphDivAtPoint(editor, range.startContainer);
    if (para && para.parentElement === editor) {
      const caretInPara = saveSelectionTextOffset(para, range);
      const paraLen = stripCaretAnchors(para.textContent ?? '').length;

      if (paraLen > 0 && caretInPara === 0) {
        const newDiv = createParagraphDiv(para);
        while (para.firstChild) {
          newDiv.appendChild(para.firstChild);
        }
        if (!para.childNodes.length) {
          para.appendChild(document.createElement('br'));
        }
        editor.insertBefore(newDiv, para.nextSibling);
        const startRange = document.createRange();
        startRange.selectNodeContents(para);
        startRange.collapse(true);
        range = startRange;
        selection.removeAllRanges();
        selection.addRange(range);
        para = para;
      } else if (caretInPara > 0 && caretInPara < paraLen) {
        splitParagraphAtCursor(editor, range);
        if (selection.rangeCount > 0) {
          const active = selection.getRangeAt(0);
          range.setStart(active.startContainer, active.startOffset);
          range.collapse(true);
        }
        para = findParagraphDivAtPoint(editor, range.startContainer) ?? para;
      } else if (paraLen > 0 && caretInPara >= paraLen) {
        splitParagraphAtCursor(editor, range);
        if (selection.rangeCount > 0) {
          const active = selection.getRangeAt(0);
          range.setStart(active.startContainer, active.startOffset);
          range.collapse(true);
        }
        para = findParagraphDivAtPoint(editor, range.startContainer) ?? para;
      }

      const targetPara =
        findParagraphDivAtPoint(editor, range.startContainer) ?? para;
      if (targetPara?.parentElement === editor) {
        const isEmpty = stripCaretAnchors(targetPara.textContent ?? '').length === 0;
        const clones = blockDivs.map((d) => cloneBlockDiv(d));

        if (isEmpty) {
          copyParagraphAttributes(targetPara, clones[0]);
          targetPara.innerHTML = clones[0].innerHTML;
          if (!targetPara.querySelector('br') && !stripCaretAnchors(targetPara.textContent ?? '').length) {
            targetPara.appendChild(document.createElement('br'));
          }
          let ref = targetPara.nextSibling;
          for (let i = 1; i < clones.length; i++) {
            editor.insertBefore(clones[i], ref);
          }
          placeCaretAtParagraphEnd(clones.length > 1 ? clones[clones.length - 1] : targetPara);
          return true;
        }

        let ref = targetPara.nextSibling;
        for (const clone of clones) {
          editor.insertBefore(clone, ref);
        }
        placeCaretAtParagraphEnd(clones[clones.length - 1]);
        return true;
      }
    }
  }

  let lastInserted: Node | null = null;
  while (temp.firstChild) {
    lastInserted = temp.firstChild;
    range.insertNode(lastInserted);
    range.setStartAfter(lastInserted);
  }
  if (lastInserted) {
    range.setStartAfter(lastInserted);
    range.collapse(true);
  }
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
};

/** @deprecated Prefer {@link insertContentAtCaret} with a restored range. */
export const insertContentAtPasteMarker = (
  editor: HTMLDivElement,
  html: string
): boolean => insertContentAtCaret(editor, html, null);

/** Move the selection to the paste marker. */
export const focusPasteMarker = (editor: HTMLElement): boolean => {
  const marker = editor.querySelector(`[${PASTE_MARKER_ATTR}]`);
  if (!marker || !editor.contains(marker)) return false;

  const selection = window.getSelection();
  if (!selection) return false;

  const range = document.createRange();
  const anchor = marker.firstChild;
  if (anchor?.nodeType === Node.TEXT_NODE) {
    range.setStart(anchor, 0);
  } else {
    range.selectNodeContents(marker);
    range.collapse(true);
  }
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
};

/** Bookmark for restoring caret after paste (includes empty paragraph lines). */
export type EditorCaretBookmark = {
  paragraphIndex: number;
  offsetInParagraph: number;
};

const getEditorParagraphDivs = (editor: HTMLDivElement): HTMLDivElement[] =>
  Array.from(editor.children).filter(
    (child): child is HTMLDivElement =>
      child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName === 'DIV'
  );

export const saveEditorCaretBookmark = (
  editor: HTMLDivElement,
  range: Range
): EditorCaretBookmark | null => {
  wrapLooseInlineContentInParagraph(editor);
  const para = findParagraphDivAtPoint(editor, range.startContainer);
  if (!para || para.parentElement !== editor) return null;
  const paragraphs = getEditorParagraphDivs(editor);
  const paragraphIndex = paragraphs.indexOf(para);
  if (paragraphIndex < 0) return null;
  return {
    paragraphIndex,
    offsetInParagraph: saveSelectionTextOffset(para, range)
  };
};

export const restoreEditorCaretBookmark = (
  editor: HTMLDivElement,
  bookmark: EditorCaretBookmark
): boolean => {
  const selection = window.getSelection();
  if (!selection) return false;

  const paragraphs = getEditorParagraphDivs(editor);
  const para = paragraphs[bookmark.paragraphIndex];
  if (!para) return false;

  const visibleLen = stripCaretAnchors(para.textContent ?? '').length;
  if (visibleLen === 0) {
    const br = para.querySelector('br');
    const range = document.createRange();
    if (br) {
      range.setStartBefore(br);
    } else {
      range.selectNodeContents(para);
    }
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  }

  restoreSelectionTextOffset(para, Math.min(bookmark.offsetInParagraph, visibleLen));
  return (
    selection.rangeCount > 0 &&
    editor.contains(selection.getRangeAt(0).commonAncestorContainer)
  );
};

/** Place caret at the start of a paragraph (textarea-like new line). */
export const placeCaretAtParagraphStart = (paragraph: HTMLDivElement): void => {
  const selection = window.getSelection();
  if (!selection) return;

  const range = document.createRange();
  const first = paragraph.firstChild;
  if (first?.nodeType === Node.TEXT_NODE) {
    range.setStart(first, 0);
  } else if (first) {
    range.setStartBefore(first);
  } else {
    range.selectNodeContents(paragraph);
  }
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
};

export const splitParagraphAtCursor = (editor: HTMLDivElement, range: Range): HTMLDivElement | null => {
  const globalOffset = saveSelectionTextOffset(editor, range);
  wrapLooseInlineContentInParagraph(editor);
  if (needsParagraphNormalize(editor)) {
    normalizeEditorParagraphs(editor);
  }
  restoreSelectionTextOffset(editor, globalOffset);

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const activeRange = selection.getRangeAt(0);

  let paraDiv = findParagraphDivAtPoint(editor, activeRange.startContainer);
  if (!paraDiv) {
    const wrapper = createParagraphDiv();
    while (editor.firstChild) {
      wrapper.appendChild(editor.firstChild);
    }
    editor.appendChild(wrapper);
    ensureParagraphCaretAnchors(editor);
    restoreSelectionTextOffset(editor, globalOffset);
    const retrySelection = window.getSelection();
    if (!retrySelection || retrySelection.rangeCount === 0) return null;
    return splitParagraphAtCursor(editor, retrySelection.getRangeAt(0));
  }

  if (isCaretAnchorNode(activeRange.startContainer) && activeRange.collapsed) {
    const newDiv = createParagraphDiv(paraDiv);
    paraDiv.parentNode?.insertBefore(newDiv, paraDiv.nextSibling);
    ensureParagraphCaretAnchors(editor);
    return newDiv;
  }

  const caretInPara = saveSelectionTextOffset(paraDiv, activeRange);
  const paraLen = stripCaretAnchors(paraDiv.textContent ?? '').length;

  // Start of a non-empty line → new empty line above (like a textarea).
  if (caretInPara === 0 && paraLen > 0) {
    const newDiv = createParagraphDiv(paraDiv);
    paraDiv.parentNode?.insertBefore(newDiv, paraDiv);
    ensureParagraphCaretAnchors(editor);
    return newDiv;
  }

  // End of line → new empty line below.
  if (caretInPara >= paraLen) {
    const newDiv = createParagraphDiv(paraDiv);
    paraDiv.parentNode?.insertBefore(newDiv, paraDiv.nextSibling);
    ensureParagraphCaretAnchors(editor);
    return newDiv;
  }

  // Middle of line → split remainder into the next paragraph.
  const afterRange = document.createRange();
  try {
    afterRange.setStart(activeRange.startContainer, activeRange.startOffset);
    afterRange.setEnd(paraDiv, paraDiv.childNodes.length);
  } catch {
    return null;
  }

  const newDiv = createParagraphDiv(paraDiv);
  const extracted = afterRange.extractContents();
  Array.from(extracted.childNodes)
    .filter((node) => !isCaretAnchorNode(node))
    .forEach((node) => newDiv.appendChild(node));

  if (!newDiv.childNodes.length) {
    newDiv.appendChild(document.createElement('br'));
  }

  paraDiv.parentNode?.insertBefore(newDiv, paraDiv.nextSibling);
  ensureParagraphCaretAnchors(editor);
  return newDiv;
};

export const applyAlignmentToRange = (
  editor: HTMLDivElement,
  offsets: { start: number; end: number },
  align: TextBlockAlign
): void => {
  normalizeEditorParagraphs(editor);
  findParagraphDivsForOffsets(editor, offsets.start, offsets.end).forEach((paragraph) => {
    applyParagraphAlignment(paragraph, align);
  });
  restoreSelectionTextOffsets(editor, offsets.start, offsets.end);
};
